import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Lambo from "../../assets/images/lambo.png";
import { FaCar, FaTruck, FaMotorcycle } from "react-icons/fa";
import { cars, locations } from '../../services/api';
import { formatTime } from "../../services/dateUtils";
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const CustomInput = React.forwardRef(
  ({ value, onClick, placeholder, isError, icon: Icon }, ref) => (
    <div
      ref={ref}
      onClick={onClick}
      style={{
        border: isError ? "1px solid #ef4444" : "1px solid gray",
        color: isError ? "#ef4444" : "gray",
      }}
      className={`w-full h-[38px] border p-1.5 rounded text-left z-50 hover:border-red-500 transition-colors duration-300 cursor-pointer ${
        isError ? "bg-red-50" : "bg-gray-50"
      } flex items-center gap-2`}
    >
      {Icon && <Icon className="text-gray-500" />}
      <span className="truncate">{value || placeholder}</span>
    </div>
  )
);

const CustomSelect = ({ value, onChange, options, placeholder, onOtherLocationChange, showOtherInput }) => (
  <div className="space-y-2">
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="w-full h-[38px] px-3 border border-gray-500 rounded bg-gray-50 text-gray-500 hover:text-gray-300 hover:bg-gray-500  appearance-none hover:border-blue-800 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-900"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M19 9l-7 7-7-7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      </div>
    </div>
    {showOtherInput && value === "other" && (
      <input
        type="text"
        placeholder="Enter location details"
        onChange={(e) => onOtherLocationChange(e.target.value)}
        className="w-full h-[38px] px-3 border border-gray-500 rounded bg-gray-50 text-gray-500 hover:border-red-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-red-500"
      />
    )}
  </div>
);

const CarRentalForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    vehicleType: "",
    pickUpLocation: "",
    dropOffLocation: "",
    pickUpDate: null,
    dropOffDate: null,
    pickUpTime: "",
    dropOffTime: "",
    age: "26+",
    couponCode: "",
    customPickupLocation: "",
    customDropoffLocation: "",
  });
  const [sameLocation, setSameLocation] = useState(true);
  const [dateErrors, setDateErrors] = useState({
    pickUp: false,
    dropOff: false,
  });

  const [datePickerOpen, setDatePickerOpen] = useState({
    pickup: false,
    dropoff: false,
  });

  const [dateTimeComplete, setDateTimeComplete] = useState({
    pickup: false,
    dropoff: false
  });

  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add state for storing dynamic pricing data  
  const [availabilityData, setAvailabilityData] = useState({
    dynamicPrices: []
  });
  
  // State for location options from API
  const [locationOptions, setLocationOptions] = useState([
    { value: "airport", label: "Airport of Mykonos (JMK)" },
    { value: "central", label: "Mykonos Central Office" },
    { value: "new-port", label: "Mykonos New Port (Tourlos)" },
    { value: "old-port", label: "Mykonos Old Port" },
    { value: "other", label: "Other Location (Hotel/Restaurant/Beach)" },
  ]);
  
  // Fetch location options from the API
  const fetchLocations = useCallback(async () => {
    try {
      // Using getActive to only get enabled locations
      const response = await locations.getActive();
      console.log('Fetched locations:', response);
      
      if (response && Array.isArray(response)) {
        // Map API locations to the format expected by the component
        const apiLocations = response.map(location => ({
          value: location._id,
          label: location.name
        }));
        
        // Always add "other" option
        apiLocations.push({ value: "other", label: "Other Location (Hotel/Restaurant/Beach)" });
        
        setLocationOptions(apiLocations);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      // Keep using the default locations if API fails
    }
  }, []);
  
  // Fetch locations when component mounts
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const vehicleCards = [
    {
      id: "cars",
      title: "Cars",
      icon: FaCar,
      categories: ["Economy", "Electric", "Standard", "Cabrio", "Special"],
    },
    {
      id: "vans",
      title: "Vans & Trucks",
      icon: FaTruck,
      categories: ["Mini Vans", "Jeep-SUV-Family", "Jeep Wrangler"],
    },
    {
      id: "atvs",
      title: "ATV / Motorbikes",
      icon: FaMotorcycle,
      categories: ["Motorbikes", "ATV-Quads"],
    },
  ];  // Function to fetch pricing data for a specific month
  const handleMonthChange = async (date) => {
    try {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      // Fetch available cars with pricing for the entire month
      const response = await cars.getAvailable({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        includeAll: true
      });
      
      if (response.data && Array.isArray(response.data)) {
        setAvailabilityData({
          dynamicPrices: response.data
        });
        console.log('Successfully loaded pricing data for', date.toLocaleDateString(), ':', response.data.length, 'entries');
      } else {
        console.warn('Unexpected response format for pricing data:', response);
        setAvailabilityData({
          dynamicPrices: []
        });
      }
    } catch (error) {
      console.error("Error fetching pricing data for month:", error);
      setAvailabilityData({
        dynamicPrices: []
      });
    }
  };

  const validateDates = (newDate, type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (type === "pickUpDate") {
      if (formData.dropOffDate && newDate > formData.dropOffDate) {
        setDateErrors((prev) => ({ ...prev, pickUp: true }));
        return false;
      }
      if (newDate < today) {
        setDateErrors((prev) => ({ ...prev, pickUp: true }));
        return false;
      }
    } else if (type === "dropOffDate") {
      if (formData.pickUpDate && newDate < formData.pickUpDate) {
        setDateErrors((prev) => ({ ...prev, dropOff: true }));
        return false;
      }
      if (newDate < today) {
        setDateErrors((prev) => ({ ...prev, dropOff: true }));
        return false;
      }
    }

    setDateErrors((prev) => ({
      ...prev,
      [type === "pickUpDate" ? "pickUp" : "dropOff"]: false,
    }));
    return true;
  };

  const handleDateChange = (date, type) => {
    if (!validateDates(date, type)) {
      return;
    }

    const isPickup = type === "pickUpDate";
    const stateKey = isPickup ? "pickup" : "dropoff";
    const otherStateKey = isPickup ? "dropoff" : "pickup";

    // Check if both date and time are selected
    const hasTime = date && date.getHours() !== 0;
    
    setDateTimeComplete((prev) => ({
      ...prev,
      [stateKey]: hasTime,
    }));

    // Show warning if time is not selected
    setTimeWarnings(prev => ({
      ...prev,
      [stateKey]: !hasTime
    }));

    setFormData((prev) => ({
      ...prev,
      [type]: date,
    }));

    // Only close if time is selected
    if (hasTime) {
      setDatePickerOpen((prev) => ({
        ...prev,
        [stateKey]: false,
        // If pickup date is set, open dropoff picker
        ...(isPickup ? { dropoff: true } : {}),
      }));
    }
  };
  const renderDayContents = (day, date) => {
    const dayDate = new Date(date);
    const isPickerOpen = datePickerOpen.pickup || datePickerOpen.dropoff;

    // Find dynamic price for this date if available
    const dynamicPriceData = availabilityData?.dynamicPrices?.find(
      (p) => new Date(p.date).toDateString() === dayDate.toDateString()
    );

    const dayPrice = dynamicPriceData?.dynamicPrice;

    return (
      <div className="relative mx-auto text-center">
        <span>{dayDate.getDate()}</span>
      </div>
    );
  };

  const handleVehicleSelect = (card) => {
    setFormData((prev) => ({ ...prev, vehicleType: card.categories }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Show closure message instead of processing the search
  alert("🏖️ We're currently closed for the 2024-2025 season. Thank you for your interest! See you next summer! ☀️");
  return;
  
  setLoading(true);
  setError(null);

  try {
    const effectivePickupLocation = formData.pickUpLocation === 'other' ? formData.customPickupLocation : formData.pickUpLocation;
    const effectiveDropoffLocation = sameLocation ? effectivePickupLocation : 
      (formData.dropOffLocation === 'other' ? formData.customDropoffLocation : formData.dropOffLocation);
    
    let dynamicPriceMap = {};
    
    // If both dates are selected, fetch dynamic pricing data
    if (formData.pickUpDate && formData.dropOffDate) {
      try {
        // Fetch dynamic pricing specifically for pickup date first
        const pickupDate = new Date(formData.pickUpDate);
        const pickupDateStr = pickupDate.toISOString().split('T')[0];
        
        console.log('Fetching pricing data for selected dates:', 
          formData.pickUpDate.toLocaleDateString(), 'to', 
          formData.dropOffDate.toLocaleDateString());
        
        // Fetch dynamic pricing for selected date range
        const availabilityResponse = await cars.getAvailable({
          startDate: formData.pickUpDate.toISOString(),
          endDate: formData.dropOffDate.toISOString(),
          includeAll: true
        });
        
        // Create a map of car IDs to dynamic prices
        if (availabilityResponse.data && availabilityResponse.data.length) {
          console.log('Received pricing data for', availabilityResponse.data.length, 'cars');
          availabilityResponse.data.forEach(car => {
            dynamicPriceMap[car._id] = car.dynamicPrice;
          });
        } else {
          console.warn('No pricing data received for the selected date range');
        }
      } catch (err) {
        console.error('Error fetching dynamic pricing:', err);
        // Continue with navigation even if pricing fetch fails
      }
    }

    navigate('/search', {
      state: {
        selectedCategory: formData.vehicleType,
        pickupLocation: effectivePickupLocation,
        dropoffLocation: effectiveDropoffLocation,
        customPickupLocation: formData.customPickupLocation,
        customDropoffLocation: formData.customDropoffLocation,
        originalPickupLocation: formData.pickUpLocation,
        originalDropoffLocation: formData.dropOffLocation,
        pickUpDate: formData.pickUpDate,
        dropOffDate: formData.dropOffDate,
        pickUpTime: formData.pickUpDate ? `${formData.pickUpDate.getHours().toString().padStart(2, '0')}:${formData.pickUpDate.getMinutes().toString().padStart(2, '0')}` : '',
        dropOffTime: formData.dropOffDate ? `${formData.dropOffDate.getHours().toString().padStart(2, '0')}:${formData.dropOffDate.getMinutes().toString().padStart(2, '0')}` : '',
        age: formData.age,
        couponCode: formData.couponCode,
        dynamicPriceMap: dynamicPriceMap // Add the dynamic price data
      }
    });
  } catch (err) {
    console.error('Error submitting form:', err);
    setError(err.message || 'An error occurred. Please try again.');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="w-full h-full content-center bg-transparent">
      <div className="max-w-[1440px] mx-auto px-2 sm:px-4">
        <motion.h2
          className="md:block hidden text-3xl lg:text-5xl pb-2 sm:pb-4 pt-6 sm:pt-10 text-left font-bold"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Mykonos Car Rentals: Discover the <br />
          <span className="font-bold">Island with Style at Best Prices</span>
        </motion.h2>
        
        {/* Closure Announcement */}
        <motion.div
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 sm:p-6 rounded-2xl shadow-xl mb-4 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="text-3xl sm:text-4xl mb-3">🏖️</div>
          <h3 className="text-xl sm:text-2xl font-bold mb-2">
            We're Closed for the Year!
          </h3>
          <p className="text-base sm:text-lg mb-2">
            Thank you for your interest in our car rental services.
          </p>
          <p className="text-sm sm:text-base opacity-90">
            We're currently closed for the 2024-2025 season and will be back next summer with our amazing fleet!
          </p>
          <p className="text-lg sm:text-xl font-semibold mt-3">
            See you in 2026! ☀️
          </p>
        </motion.div>
        <motion.form
          onSubmit={handleSubmit}
          className="p-2 sm:p-4 md:p-6 md:mt-0 mt-24 bg-white shadow-md rounded-md border w-full relative opacity-75"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 0.75, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Closure Notice Overlay */}
          <div className="absolute inset-0 bg-orange-100 bg-opacity-80 z-40 flex items-center justify-center rounded-md">
            <div className="text-center p-4">
              <div className="text-4xl mb-3">🏖️</div>
              <p className="text-orange-800 font-semibold text-lg mb-2">
                Form Temporarily Disabled
              </p>
              <p className="text-orange-700 text-sm">
                We're closed for the 2024-2025 season
              </p>
            </div>
          </div>

          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 z-50 flex items-center justify-center">
              <LoadingSpinner message="Checking availability..." />
            </div>
          )}

          {error && (
            <motion.div
              className="mb-4 bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-center">{error}</p>
            </motion.div>
          )}

          {/* Top Section - Vehicle Selection and Car Image */}
          <div className="flex flex-col lg:flex-row justify-between items-start gap-2 sm:gap-6">
            <div className="w-full lg:w-3/4">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-blue-800 text-left mb-2 sm:mb-4">
Your Trusted Car Rental in Mykonos since 2020              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-3 md:gap-4">
                {vehicleCards.map((card, index) => (
                  <motion.button
                    type="button"
                    key={card.id}
                    className={`flex items-center p-2.5 sm:p-3 md:p-4 border rounded-lg cursor-pointer ${
                      JSON.stringify(formData.vehicleType) === JSON.stringify(card.categories)
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200"
                    } hover:border-red-500 transition-all duration-300 ${
                      index === vehicleCards.length - 1 ? "col-span-2 sm:col-span-1" : ""
                    }`}
                    onClick={() => handleVehicleSelect(card)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <card.icon className="text-lg sm:text-xl md:text-2xl text-gray-600 mr-2 md:mr-3 flex-shrink-0" />
                    <span className="font-medium text-gray-700 text-sm sm:text-base">
                      {card.title}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
            <div className="hidden lg:block lg:w-1/4 lg:min-w-[200px]">
              <img
                src={Lambo}
                alt="Car Icon"
                className="w-full h-auto max-h-[120px] object-contain animate-slide-in"
              />
            </div>
          </div>

          {/* Input Sections */}
          <div className="space-y-2 sm:space-y-4 md:space-y-6">
            {/* Location Container */}
            <div className="p-2 sm:p-4 rounded-lg">
              <div className="flex justify-end mb-1 sm:mb-2">
                <label className="flex items-center text-xs sm:text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={sameLocation}
                    onChange={(e) => setSameLocation(e.target.checked)}
                    className="mr-1.5 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4"
                  />
                  <span className="whitespace-nowrap">Same return location</span>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <div className={`relative ${sameLocation ? "md:col-span-2" : ""}`}>
                  <CustomSelect
                    value={formData.pickUpLocation}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        pickUpLocation: e.target.value,
                        customPickupLocation: e.target.value !== 'other' ? '' : formData.customPickupLocation
                      });
                    }}
                    onOtherLocationChange={(value) => {
                      setFormData({
                        ...formData,
                        customPickupLocation: value
                      });
                    }}
                    options={locationOptions}
                    placeholder="Pick-up Location"
                    showOtherInput={true}
                  />
                </div>
                {!sameLocation && (
                  <div className="relative">
                    <CustomSelect
                      value={formData.dropOffLocation}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          dropOffLocation: e.target.value,
                          customDropoffLocation: e.target.value !== 'other' ? '' : formData.customDropoffLocation
                        });
                      }}
                      onOtherLocationChange={(value) => {
                        setFormData({
                          ...formData,
                          customDropoffLocation: value
                        });
                      }}
                      options={locationOptions}
                      placeholder="Drop-off Location"
                      showOtherInput={true}
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-4 w-full">
                <div className="w-full relative">                  <DatePicker
                    selected={formData.pickUpDate}
                    onChange={(date) => handleDateChange(date, "pickUpDate")}
                    onMonthChange={handleMonthChange}
                    customInput={<CustomInput isError={dateErrors.pickUp} />}
                    dateFormat="dd/MM/yyyy HH:mm"
                    minDate={new Date()}
                    placeholderText="Pick-up Date & Time"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    open={datePickerOpen.pickup}
                    onClickOutside={() => {
                      if (dateTimeComplete.pickup) {
                        setDatePickerOpen(prev => ({ ...prev, pickup: false }));
                      }
                    }}
                    onInputClick={() => setDatePickerOpen(prev => ({ 
                      pickup: true,
                      dropoff: false 
                    }))}
                    renderDayContents={renderDayContents}
                    wrapperClassName="w-full bg-gray-50 border-gray-300 hover:border-red-500 ease-in-out duration-300"
                    popperClassName="date-picker-popper"
                    popperPlacement="bottom-start"
                    popperProps={{
                      strategy: "fixed",
                      modifiers: [
                        {
                          name: "preventOverflow",
                          options: {
                            rootBoundary: "viewport",
                            padding: 8
                          }
                        }
                      ]
                    }}
                  />
                  {timeWarnings.pickup && (
                    <p className="text-red-500 text-xs mt-1">Please select a time.</p>
                  )}
                </div>
                <div className="w-full relative">
                  <DatePicker
                    selected={formData.dropOffDate}
                    onChange={(date) => handleDateChange(date, "dropOffDate")}
                    customInput={
                      <CustomInput
                        placeholder="Drop-off Date & Time"
                        isError={dateErrors.dropOff}
                      />
                    }
                    dateFormat="dd/MM/yyyy HH:mm"
                    placeholderText="Drop-off Date & Time"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    minDate={formData.pickUpDate || new Date()}
                    open={datePickerOpen.dropoff}
                    onClickOutside={() => {
                      if (dateTimeComplete.dropoff) {
                        setDatePickerOpen(prev => ({ ...prev, dropoff: false }));
                      }
                    }}
                    onInputClick={() => setDatePickerOpen(prev => ({ 
                      pickup: false, 
                      dropoff: true 
                    }))}
                    wrapperClassName="w-full bg-gray-50 border-gray-300 hover:border-red-500 ease-in-out duration-300"
                    popperClassName="date-picker-popper"
                    popperPlacement="bottom-start"
                    // Fix for "fn is not a function" error
                    popperProps={{
                      strategy: "fixed",
                      modifiers: [
                        {
                          name: "preventOverflow",
                          options: {
                            rootBoundary: "viewport",
                            padding: 8
                          }
                        }
                      ]
                    }}
                  />
                  {timeWarnings.dropoff && (
                    <p className="text-red-500 text-xs mt-1">Please select a time.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="bg-gray-50 p-2 sm:p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
                <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
                  <span className="text-gray-600 text-sm sm:text-base">
                    I am
                  </span>
                  <select
                    value={formData.age}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, age: e.target.value }))
                    }
                    className="mx-1.5 sm:mx-2 p-1 border rounded hover:border-red-500 text-blue-800 transition-colors duration-300 text-sm sm:text-base"
                  >
                    {Array.from({ length: 9 }, (_, i) => 18 + i).map((age) => (
                      <option key={age} value={age}>
                        {age}
                      </option>
                    ))}
                    <option value="26+">26+</option>
                  </select>
                  <span className="text-gray-600 text-sm sm:text-base">
                    years old
                  </span>
                                    <input
                                      type="text"
                                      placeholder="Coupon Code (Optional)"
                                      value={formData.couponCode}
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          couponCode: e.target.value,
                                        }))
                                      }
                                      className="p-1.5 sm:p-2 border rounded hover:border-red-500 text-blue-800 transition-colors duration-300 w-full sm:w-40 text-sm sm:text-base"
                                    />
                </div>

                <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">


                  <motion.button
                    type="submit"
                    disabled={true}
                    className="bg-orange-400 text-orange-800 text-sm sm:text-base py-1.5 sm:py-2 px-3 sm:px-6 rounded cursor-not-allowed border-2 border-orange-500 font-semibold whitespace-nowrap w-full sm:w-auto flex-shrink-0"
                  >
                    🏖️ Closed for Season - See You in 2026! ☀️
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </motion.form>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideIn {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }

          .animate-slide-in {
            animation: slideIn 1s ease-in-out;
          }

          @media (max-width: 640px) {
            .react-datepicker-wrapper {
              width: 100%;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CarRentalForm;